import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Button,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Launch as LaunchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Psychology as SkillIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FollowButton from 'components/common/buttons/FollowButton';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
  },
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(2),
}));

// Helper function to get action type display name
const getActionTypeDisplay = (code) => {
  const actionTypes = {
    'research': 'Research',
    'innovation': 'Innovation',
    'ria': 'Research and Innovation',
    'both': 'Research and Innovation',
    'csa': 'Coordination and Support Action',
    'cofund': 'Co-Fund Action',
    'ppi': 'Public Procurement of Innovation',
  };
  return actionTypes[code] || code;
};

const DeadlineChip = styled(Chip)(({ urgent, theme }) => ({
  fontWeight: 'bold',
  ...(urgent && {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    animation: 'pulse 2s infinite',
  }),
}));

const COOPERATION_TYPE_LABELS = {
  EUR: 'European',
  INT: 'International',
  MUL: 'Multilateral',
  BIL: 'Bilateral',
  NAT: 'National',
};

const COOPERATION_TYPE_COLORS = {
  EUR: 'primary',
  INT: 'secondary',
  MUL: 'success',
  BIL: 'info',
  NAT: 'warning',
};

const DEFAULT_SORT = 'cooperation_sort,program__agency__category,-created';

// keyword: only calls matching these words (their own text, program or agency) — under the chosen
// agency / program, or, with no agency, across all of them. onClearKeyword drops the filter.
// latest: the calls added in the last LATEST_DAYS days, across every agency, newest first (the "New calls"
// card at the top of the funding page leads here).
export const LATEST_DAYS = 30;
const CallsList = ({ selectedAgency, selectedProgram, cooperationType, callStatuses = [], keyword = '', latest = false, latestRelevant = false, onClearLatestRelevant, onClearKeyword, onBack, onPinCall, pinnedIds = [] }) => {
  const latestMode = !selectedAgency && latest;
  const keywordMode = !selectedAgency && !latestMode && Boolean(keyword);
  const allAgencies = keywordMode || latestMode;
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(latest ? '-created' : DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const [bookmarkedCalls, setBookmarkedCalls] = useState(new Set());
  const [, setSelectedCall] = useState(null);
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);

  const pageSize = 12;

  // Fetch available categories from funding calls
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        // Using funding/call-categories endpoint for call categories
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'}/funding/call-categories/`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          // If data has results array (paginated), use it; otherwise use data directly
          const categories = data.results || data || [];
          setAvailableCategories(categories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch available skills
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'}/skills/`,
          { credentials: 'include' }
        );
        // console.log('Fetched skills:', response);
        if (response.ok) {
          const data = await response.json();
          // Since pagination is disabled, data is an array directly
          
          setAvailableSkills(Array.isArray(data) ? data : (data.results || []));
        }
      } catch (err) {
        console.error('Error fetching skills:', err);
      } finally {
        setSkillsLoading(false);
      }
    };
    fetchSkills();
  }, []);

  // Debounce search term - only update after user stops typing for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedAgency || allAgencies) {
      fetchCalls();
    }
  }, [selectedAgency, selectedProgram, keyword, latest, latestRelevant, cooperationType, callStatuses, page, debouncedSearchTerm, sortBy, selectedCategories, selectedSkills]);

  const fetchCalls = async () => {
    
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        ordering: sortBy,
      });
      if (keyword && !latestMode) params.append('q', keyword);
      if (latestMode) params.append('added_within_days', String(LATEST_DAYS));
      if (latestMode && latestRelevant) params.append('relevant', '1');
      if (!allAgencies) params.append('agency_id', selectedAgency.id);

      if (selectedProgram) {
        params.append('program', selectedProgram.id);
      }

      if (debouncedSearchTerm.trim()) {
        params.append('search', debouncedSearchTerm.trim());
      }

      // Propagate cooperation type selected on agency page
      if (cooperationType) {
        params.append('cooperation_type', cooperationType);
      }

      // Propagate call status filter selected on agency page
      if (callStatuses.length > 0) {
        params.append('statuses', callStatuses.join(','));
      }

      // Add category filter if categories are selected
      if (selectedCategories.length > 0) {
        params.append('categories', selectedCategories.map(cat => cat.id).join(','));
      }

      // Add skill filter if skills are selected
      if (selectedSkills.length > 0) {
        params.append('skills', selectedSkills.map(skill => skill.id).join(','));
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'}/funding/calls/?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch calls');
      }

      const data = await response.json();
      setCalls(data.results || []);
      setTotalCalls(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
    } catch (err) {
      setError(err.message);
      console.error('Error fetching calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline specified';
    
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const timeDiff = deadlineDate - now;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) {
      return 'Deadline passed';
    } else if (daysDiff === 0) {
      return 'Due today';
    } else if (daysDiff === 1) {
      return 'Due tomorrow';
    } else if (daysDiff <= 30) {
      return `${daysDiff} days left`;
    } else {
      return deadlineDate.toLocaleDateString();
    }
  };


  const handleCallSelect = (call) => {
    setSelectedCall(call);
    // setCurrentStep(3); // Optional: Update step if needed
    navigate(`/funding/call/${call.id}`, {
      state: {
        selectedAgency,
        selectedProgram,
        selectedCall: call, 
    },
  });
  };

  const isDeadlineUrgent = (deadline) => {
    if (!deadline) return false;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const daysDiff = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    return daysDiff <= 30 && daysDiff >= 0;
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // Page reset is handled in the debounce useEffect
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  if (!selectedAgency && !allAgencies) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6">Please select a funding agency first</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading calls: {error}
        </Alert>
        <Button variant="contained" onClick={fetchCalls}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with Agency Info */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'primary.50' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {latestMode
                ? (latestRelevant ? 'Latest calls for you' : 'Latest calls')
                : keywordMode
                ? `Calls matching “${keyword}”`
                : selectedProgram
                ? `${selectedProgram.name} Calls`
                : `Funding Calls from ${selectedAgency.name}`
              }
            </Typography>
            {latestMode && latestRelevant && (
              <Chip color="primary" sx={{ mb: 1.5 }} label="Only calls relevant to your profile" onDelete={onClearLatestRelevant || undefined} />
            )}
            {keyword && !keywordMode && (
              <Chip color="primary" sx={{ mb: 1.5 }} label={`Only calls matching “${keyword}”`}
                onDelete={onClearKeyword || undefined} />
            )}
            <Box display="flex" alignItems="center" gap={2}>
              {allAgencies
                ? <Chip icon={<BusinessIcon />} label="All agencies and programs" />
                : <Chip icon={<BusinessIcon />} label={selectedAgency.country} />}
              {selectedProgram && (
                <Chip icon={<AccessTimeIcon />} label={selectedProgram.abbreviation}  />
              )}
              <Chip icon={<AccessTimeIcon />} label={`${totalCalls} total calls`} />
            </Box>
          </Box>
          <Button variant="outlined" onClick={onBack}>
            {allAgencies ? 'Back to Agencies' : 'Back to Programs'}
          </Button>
        </Box>
        
        {latestMode ? (
          <Typography variant="body1" color="text.secondary">
            The calls added to Otable in the last {LATEST_DAYS} days, newest first, from every agency. Each card shows its agency and program.
          </Typography>
        ) : keywordMode ? (
          <Typography variant="body1" color="text.secondary">
            Every call whose text, program or agency mentions these words. Each card shows its agency and program.
          </Typography>
        ) : selectedProgram ? (
          <Typography variant="body1" color="text.secondary">
            {selectedProgram.description}
          </Typography>
        ) : selectedAgency.description && (
          <Typography variant="body1" color="text.secondary">
            {selectedAgency.description}
          </Typography>
        )}
      </Paper>

      {/* Search and Filter Controls */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Search Box - Full Width */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search calls by title, scope, or keywords..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              options={availableCategories}
              value={selectedCategories}
              onChange={(event, newValue) => {
                event.stopPropagation();
                event.preventDefault();
                setSelectedCategories(newValue);
                setPage(1);
              }}
              getOptionLabel={(option) => option.name}
              loading={categoriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Categories"
                  placeholder="Select categories..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <CategoryIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Typography variant="body2">{option.name}</Typography>
                    <Chip label={option.calls_count} size="small" sx={{ ml: 1 }} />
                  </Box>
                </li>
              )}
            />
          </Grid>

          {/* Skill Filter */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              options={availableSkills}
              value={selectedSkills}
              onChange={(event, newValue) => {
                event.stopPropagation();
                event.preventDefault();

                setSelectedSkills(newValue);
                setPage(1);
              }}
              getOptionLabel={(option) => option.name}
              loading={skillsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Skills"
                  placeholder="Select skills..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SkillIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))
              }
            />
          </Grid>

          {/* Sort By */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={handleSortChange}
                startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value={DEFAULT_SORT}>By Type &amp; Agency</MenuItem>
                <MenuItem value="-created">Newest First</MenuItem>
                <MenuItem value="created">Oldest First</MenuItem>
                <MenuItem value="closes_at">Deadline (Earliest)</MenuItem>
                <MenuItem value="-closes_at">Deadline (Latest)</MenuItem>
                <MenuItem value="title">Title A-Z</MenuItem>
                <MenuItem value="-title">Title Z-A</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Results Count */}
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" height="100%">
              <FilterIcon color="action" />
              <Typography variant="body2" color="text.secondary">
                {totalCalls} call{totalCalls !== 1 ? 's' : ''} found
              </Typography>
            </Stack>
          </Grid>

          {/* Active Filters Display */}
          {(selectedCategories.length > 0 || selectedSkills.length > 0) && (
            <Grid item xs={12}  >
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} >
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Active filters:
                </Typography>
                {selectedCategories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    onDelete={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedCategories(selectedCategories.filter(c => c.id !== category.id));
                      setPage(1);
                    }}
                    size="small"
                    color="primary"
                  />
                ))}
                {selectedSkills.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    onDelete={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      setSelectedSkills(selectedSkills.filter(s => s.id !== skill.id));
                      setPage(1);
                    }}
                    size="small"
                    color="secondary"
                  />
                ))}
                {(selectedCategories.length > 0 || selectedSkills.length > 0) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCategories([]);
                      setSelectedSkills([]);
                      setPage(1);
                    }}
                    sx={{ ml: 1 }}
                  >
                    Clear All
                  </Button>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Calls Grid */}
      {calls.length === 0 ? (
        <Box textAlign="center" mt={4}>
          <Typography variant="h6" color="text.secondary">
            {latestMode ? `No calls were added in the last ${LATEST_DAYS} days` : keywordMode ? `No calls match “${keyword}”` : keyword ? `No calls of ${selectedProgram?.name || selectedAgency.name} match “${keyword}”` : `No calls found for ${selectedAgency.name}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search terms or filters, or check back later for new opportunities
          </Typography>
          {!allAgencies && selectedAgency.website && (
            <Button variant="outlined" size="small" sx={{ mt: 2 }} href={selectedAgency.website}
              target="_blank" rel="noopener noreferrer">
              See {selectedAgency.acronym || selectedAgency.name}'s own funding page
            </Button>
          )}
        </Box>
      ) : (
        <>
          {sortBy === DEFAULT_SORT ? (
            (() => {
              const groups = [];
              let lastType = undefined;
              calls.forEach((call) => {
                const ct = call.program_cooperation_type || null;
                if (ct !== lastType) {
                  groups.push({ type: ct, calls: [call] });
                  lastType = ct;
                } else {
                  groups[groups.length - 1].calls.push(call);
                }
              });
              return groups.map((group) => (
                <Box key={group.type || 'unclassified'} sx={{ mb: 4}}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, px: 2}}>
                    <Chip
                      label={COOPERATION_TYPE_LABELS[group.type] || 'Unclassified'}
                      color={COOPERATION_TYPE_COLORS[group.type] || 'default'}
                      size="medium"
                      sx={{ fontWeight: 700, fontSize: '0.85rem' }}
                    />
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {group.calls.length} call{group.calls.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                      gap: 3,
                    }}
                  >
                    {group.calls.map((call) => (
                      <StyledCard key={call.id} onClick={() => handleCallSelect(call)} sx={{ cursor: 'pointer' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} mb={1}>
                            <FollowButton
                              contentType="call"
                              objectId={call.id}
                              size="small"
                              variant="outlined"
                            />
                            {onPinCall && (
                              <Tooltip title={pinnedIds.includes(call.id) ? 'Pinned' : 'Pin to Quick Access'}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); onPinCall(call); }}
                                >
                                  {pinnedIds.includes(call.id) ? <PinIcon /> : <PinOutlinedIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flexGrow: 1, mr: 1 }}>
                              {call.title}
                              {call.external_url && (
                                <Tooltip title="View on Agency Website">
                                  <IconButton
                                    color="default"
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); window.open(call.external_url, '_blank', 'noopener,noreferrer'); }}
                                  >
                                    <LaunchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Typography>
                          </Box>
                          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                            <Chip label={call.code} size="small" variant="outlined" />
                            {call.type_of_action && (
                              <Chip label={getActionTypeDisplay(call.type_of_action)} size="small" color="primary" />
                            )}
                            {call.agency_name && (
                              <Chip label={call.agency_name} size="small" variant="outlined" color="default" />
                            )}
                          </Box>
                          {call.program_name && (
                            <Box mb={2}>
                              <Chip label={call.program_name} size="small" color="secondary" variant="outlined"
                                sx={{ maxWidth: '100%', '& .MuiChip-label': { textOverflow: 'ellipsis', overflow: 'hidden' } }}
                              />
                            </Box>
                          )}
                          <Box display="flex" gap={2} mb={2}>
                            {call.closes_at && (
                              <DeadlineChip icon={<CalendarIcon />} label={formatDeadline(call.closes_at)}
                                size="small" urgent={isDeadlineUrgent(call.closes_at)} />
                            )}
                            {call.budget_display && call.budget_display !== 'Not specified' && (
                              <Tooltip title="Budget">
                                <Chip icon={<EuroIcon />} label={call.budget_display} size="small" variant="outlined" />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                            {truncateText(call.scope)}
                          </Typography>
                        </CardContent>
                      </StyledCard>
                    ))}
                  </Box>
                </Box>
              ));
            })()
          ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 3,
            }}
          >
            {calls.map((call) => (
              <StyledCard key={call.id} onClick={() => handleCallSelect(call)} sx={{ cursor: 'pointer' }}>
                <CardContent>
                  {/* Follow / Pin */}
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} mb={1}>
                    <FollowButton
                      contentType="call"
                      objectId={call.id}
                      size="small"
                      variant="outlined"
                    />
                    {onPinCall && (
                      <Tooltip title={pinnedIds.includes(call.id) ? 'Pinned' : 'Pin to Quick Access'}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onPinCall(call); }}
                        >
                          {pinnedIds.includes(call.id) ? <PinIcon /> : <PinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                    {/* Header with Title and website */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flexGrow: 1, mr: 1 }}>
                        {call.title}
                        {call.external_url && (
                          <Tooltip title="View on Agency Website">
                            <IconButton
                              color="default"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); window.open(call.external_url, '_blank', 'noopener,noreferrer'); }}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Typography>
                    </Box>

                    {/* Call ID and Type */}
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      <Chip label={call.code} size="small" variant="outlined" />
                      {call.type_of_action && (
                        <Chip label={getActionTypeDisplay(call.type_of_action)} size="small" color="primary" />
                      )}
                      {call.program_cooperation_type && (
                        <Chip
                          label={COOPERATION_TYPE_LABELS[call.program_cooperation_type] || call.program_cooperation_type}
                          size="small"
                          color={COOPERATION_TYPE_COLORS[call.program_cooperation_type] || 'default'}
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Program Information */}
                    {call.program_name && (
                      <Box mb={2}>
                        <Chip
                          label={call.program_name}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              textOverflow: 'ellipsis',
                              overflow: 'hidden'
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Deadline and Budget */}
                    <Box display="flex" gap={2} mb={2}>
                      {call.closes_at && (
                        <DeadlineChip
                          icon={<CalendarIcon />}
                          label={formatDeadline(call.closes_at)}
                          size="small"
                          urgent={isDeadlineUrgent(call.closes_at)}
                        />
                      )}
                      {call.budget_display && call.budget_display !== 'Not specified' && (
                        <Tooltip title="Budget">
                          <Chip
                            icon={<EuroIcon />}
                            label={call.budget_display}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Scope Preview */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                      {truncateText(call.scope)}
                    </Typography>

                  </CardContent>
                </StyledCard>
            ))}
          </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};


export default CallsList;